/**
 * Symbol Autocomplete Component
 *
 * Provides symbol search with autocomplete functionality.
 * Loads symbols from both user's positions and the instruments table.
 */

import React, { useState, useMemo, useEffect, useRef } from 'react';
import {
  View,
  Text,
  TextInput,
  TouchableOpacity,
  StyleSheet,
  ScrollView,
  ActivityIndicator,
} from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { useTranslation } from 'react-i18next';
import { useTheme } from '../../contexts/ThemeContext';
import { useLanguage } from '../../contexts/LanguageContext';
import { usePositions } from '../../services/api/queries/usePortfolio';
import { financialDataAPI, type Instrument } from '../../services/api/financialDataApi';
import { getToken } from '../../services/tokenStorage';
import { getLocalizedInstrumentName } from '../../utils/translationHelpers';

// Exchange priority for sorting exact symbol matches
// Lower number = higher priority (shown first)
const EXCHANGE_PRIORITY: Record<string, number> = {
  US: 1,
  NASDAQ: 1,
  NYSE: 1,
  LSE: 2,
  HK: 3,
  SHE: 4, // Shenzhen
  SHG: 4, // Shanghai
  T: 5, // Japan
  TW: 6, // Taiwan
  KRX: 7, // Korea
  KQ: 7, // KOSDAQ (Korea)
  KSE: 7, // Korea Stock Exchange
  PA: 8, // Paris (Euronext)
  XETRA: 8, // Deutsche Börse
  // Other exchanges get priority 99
};

function getExchangePriority(exchange?: string): number {
  if (!exchange) return 99;
  return EXCHANGE_PRIORITY[exchange] ?? 99;
}

export interface SymbolOption {
  id?: number; // Instrument id when source='instrument'. Lets the consumer
  // honor the EXACT instrument the user picked (not just symbol text).
  symbol: string;
  name?: string;
  exchange?: string;
  source: 'position' | 'instrument';
  currency?: string;
  sector?: string;
  industry?: string;
  asset_type?: string;
}

interface SymbolAutocompleteProps {
  value: string;
  onChangeText: (text: string) => void;
  placeholder?: string;
  error?: string;
  editable?: boolean;
  onSymbolSelect?: (option: SymbolOption) => void;
  initialCurrency?: string; // Currency when symbol is pre-selected (e.g., from navigation)
  readOnly?: boolean; // When true, show symbol as read-only text (from position detail)
  instrumentName?: string; // Instrument name to display (from props or after selection)
  positionsOnly?: boolean; // When true, only search within existing positions, not instruments API
  autoFilled?: boolean; // When true, value is auto-filled (e.g., from AI extraction), don't suppress suggestions
}

export function SymbolAutocomplete({
  value,
  onChangeText,
  placeholder,
  error,
  editable = true,
  onSymbolSelect,
  initialCurrency,
  readOnly = false,
  instrumentName: propInstrumentName,
  positionsOnly = false,
  autoFilled = false,
}: SymbolAutocompleteProps) {
  const { theme } = useTheme();
  const { t } = useTranslation();
  const { language } = useLanguage();
  const styles = useMemo(() => createThemedStyles(theme), [theme]);

  const [query, setQuery] = useState(value);
  const [suggestions, setSuggestions] = useState<SymbolOption[]>([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [loading, setLoading] = useState(false);
  const [hasSelectedInitial, setHasSelectedInitial] = useState(false); // Track if initial value was selected
  const [userHasFocused, setUserHasFocused] = useState(false); // Track if user has focused the input
  const [selectedInstrumentName, setSelectedInstrumentName] = useState<string | undefined>(
    propInstrumentName
  );

  // Update selectedInstrumentName when prop changes
  useEffect(() => {
    if (propInstrumentName) {
      setSelectedInstrumentName(propInstrumentName);
    }
  }, [propInstrumentName]);

  const { data: positions } = usePositions();

  // Track the latest query to avoid race conditions
  const latestQueryRef = useRef<string>('');
  const searchTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  // Update internal state when value prop changes
  useEffect(() => {
    setQuery(value);
  }, [value]);

  // When initialized with value and initialCurrency/instrumentName, treat as pre-selected
  // This happens when navigating from InstrumentDetail/PositionDetail with symbol + currency
  useEffect(() => {
    if (value && (initialCurrency || propInstrumentName)) {
      // Check if this symbol matches a position first (for source info)
      const matchedPosition = positionSymbols.find((opt) => opt.symbol === value);

      setHasSelectedInitial(true); // Mark as pre-selected
      if (onSymbolSelect) {
        onSymbolSelect({
          symbol: value,
          currency: initialCurrency,
          name: matchedPosition?.name || propInstrumentName,
          source: matchedPosition ? 'position' : 'instrument',
        });
      }
    }
    // Only run on mount, not when value/initialCurrency changes during editing
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Get unique symbols from positions
  const positionSymbols = useMemo(() => {
    if (!positions) return [];
    const uniqueSymbols = new Map<
      string,
      {
        name: string;
        exchange?: string;
        currency?: string;
        sector?: string;
        industry?: string;
        asset_type?: string;
      }
    >();
    positions.forEach((pos) => {
      if (!uniqueSymbols.has(pos.symbol)) {
        const localizedName = getLocalizedInstrumentName(
          pos.instrument_name,
          pos.instrument_name_sch,
          pos.instrument_name_tch,
          language
        );
        uniqueSymbols.set(pos.symbol, {
          name: localizedName || undefined,
          exchange: pos.instrument_exchange || undefined,
          currency: pos.currency,
          // Use position-level override if exists, otherwise use instrument-level value
          sector: pos.sector || pos.instrument_sector || undefined,
          industry: pos.industry || pos.instrument_industry || undefined,
          asset_type: pos.asset_type || pos.instrument_asset_type || undefined,
        });
      }
    });
    return Array.from(uniqueSymbols.entries()).map(([symbol, data]) => ({
      symbol,
      name: data.name,
      exchange: data.exchange,
      source: 'position' as const,
      currency: data.currency,
      sector: data.sector,
      industry: data.industry,
      asset_type: data.asset_type,
    }));
  }, [positions, language]);

  // Track if user has manually edited the field (changed from initial value)
  const [userHasEdited, setUserHasEdited] = useState(false);

  // Search instruments API
  const searchInstruments = async (searchQuery: string) => {
    // Update the ref to track this query
    latestQueryRef.current = searchQuery;

    if (searchQuery.length < 1) {
      setSuggestions([]);
      setLoading(false);
      return;
    }

    // First, filter positions locally
    const matchingPositions = positionSymbols.filter((opt) =>
      opt.symbol.toLowerCase().includes(searchQuery.toLowerCase())
    );

    console.log('[SymbolAutocomplete] Search query:', searchQuery);
    console.log(
      '[SymbolAutocomplete] Matching positions:',
      matchingPositions.map((p) => ({ symbol: p.symbol, exchange: p.exchange, name: p.name }))
    );

    // If positionsOnly mode, only show matching positions
    if (positionsOnly) {
      setSuggestions(matchingPositions);
      setLoading(false);
      return;
    }

    // Search instruments API (always do this, even if we have matching positions)
    setLoading(true);
    try {
      const token = await getToken();
      const instruments = await financialDataAPI.searchInstruments(searchQuery, 50, token);

      // Check if this is still the latest query
      if (searchQuery !== latestQueryRef.current) {
        return;
      }

      const instrumentOptions: SymbolOption[] = instruments.map((inst) => ({
        id: inst.id,
        symbol: inst.symbol,
        name: getLocalizedInstrumentName(inst.company_name, inst.name_sch, inst.name_tch, language),
        exchange: inst.exchange,
        source: 'instrument',
        currency: inst.currency,
        sector: inst.sector,
        industry: inst.industry,
        asset_type: inst.asset_type,
      }));

      console.log(
        '[SymbolAutocomplete] Instrument options:',
        instrumentOptions.slice(0, 5).map((i) => ({ symbol: i.symbol, exchange: i.exchange }))
      );

      // Combine matching positions (if any) with instrument results

      // Combine matching positions (if any) with instrument results
      // Remove duplicates (if an instrument matches a position symbol)
      const positionSymbolsSet = new Set(matchingPositions.map((p) => p.symbol));
      const uniqueInstruments = instrumentOptions.filter(
        (inst) => !positionSymbolsSet.has(inst.symbol)
      );

      // Combine all suggestions
      const allSuggestions = [...matchingPositions, ...uniqueInstruments];

      // Sort all suggestions by relevance
      const queryLower = searchQuery.toLowerCase();
      allSuggestions.sort((a, b) => {
        // Position source (highest priority - positions always shown before instruments)
        if (a.source === 'position' && b.source !== 'position') return -1;
        if (a.source !== 'position' && b.source === 'position') return 1;

        // Within same source, sort by relevance
        // Exact symbol match
        const aSymbolExact = a.symbol.toLowerCase() === queryLower;
        const bSymbolExact = b.symbol.toLowerCase() === queryLower;

        // When both have exact symbol match, sort by exchange priority
        if (aSymbolExact && bSymbolExact) {
          const aPriority = getExchangePriority(a.exchange);
          const bPriority = getExchangePriority(b.exchange);
          if (aPriority !== bPriority) return aPriority - bPriority;
        }
        if (aSymbolExact && !bSymbolExact) return -1;
        if (!aSymbolExact && bSymbolExact) return 1;

        // Exact name match (same priority as symbol exact)
        const aNameExact = a.name?.toLowerCase() === queryLower;
        const bNameExact = b.name?.toLowerCase() === queryLower;
        if (aNameExact && !bNameExact) return -1;
        if (!aNameExact && bNameExact) return 1;

        // Symbol prefix match (higher priority than exchange)
        const aPrefix = a.symbol.toLowerCase().startsWith(queryLower);
        const bPrefix = b.symbol.toLowerCase().startsWith(queryLower);
        if (aPrefix && !bPrefix) return -1;
        if (!aPrefix && bPrefix) return 1;

        // When both have prefix match, sort by exchange priority first
        if (aPrefix && bPrefix) {
          const aPriority = getExchangePriority(a.exchange);
          const bPriority = getExchangePriority(b.exchange);
          if (aPriority !== bPriority) return aPriority - bPriority;
        }

        // Company name prefix match
        const aNamePrefix = a.name?.toLowerCase().startsWith(queryLower);
        const bNamePrefix = b.name?.toLowerCase().startsWith(queryLower);
        if (aNamePrefix && !bNamePrefix) return -1;
        if (!aNamePrefix && bNamePrefix) return 1;

        // If same relevance level, sort by exchange priority first
        // (popular exchanges shown first: US, LSE, HK, etc.)
        const aPriority = getExchangePriority(a.exchange);
        const bPriority = getExchangePriority(b.exchange);
        if (aPriority !== bPriority) return aPriority - bPriority;

        // Same relevance level, sort by symbol alphabetically
        return a.symbol.localeCompare(b.symbol);
      });

      console.log(
        '[SymbolAutocomplete] Final suggestions (top 5):',
        allSuggestions
          .slice(0, 5)
          .map((s) => ({ symbol: s.symbol, exchange: s.exchange, source: s.source }))
      );

      setSuggestions(allSuggestions.slice(0, 30));
    } catch (error) {
      // Check if this is still the latest query
      if (searchQuery !== latestQueryRef.current) {
        return;
      }

      setSuggestions([]);
    } finally {
      // Only hide loading if this is still the latest query
      if (searchQuery === latestQueryRef.current) {
        setLoading(false);
      }
    }
  };

  // Debounced search - ONLY depends on query, not positionSymbols
  useEffect(() => {
    // Clear any existing timeout
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }

    // Don't search if query is empty OR if in readOnly mode
    if (!query.trim() || readOnly) {
      setLoading(false);
      setSuggestions([]);
      setShowSuggestions(false);
      return;
    }

    // Don't show suggestions if we have a pre-selected initial value AND user hasn't edited
    // Wait for user to manually type or interact
    // Exception: if autoFilled is true (e.g., AI extraction), allow suggestions
    if (hasSelectedInitial && !userHasEdited && !autoFilled) {
      setLoading(false);
      setSuggestions([]);
      setShowSuggestions(false);
      return;
    }

    // For autoFilled values, don't auto-show suggestions on mount
    // Only show when user explicitly interacts (focus, types, etc.)
    if (autoFilled && !userHasEdited && !userHasFocused) {
      setLoading(false);
      setSuggestions([]);
      setShowSuggestions(false);
      return;
    }

    // Show suggestions immediately with loading state
    setShowSuggestions(true);
    setLoading(true);

    // Set new timeout
    searchTimeoutRef.current = setTimeout(() => {
      searchInstruments(query);
    }, 500);

    // Cleanup
    return () => {
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
      }
    };
  }, [query, hasSelectedInitial, readOnly, autoFilled, userHasEdited]); // Added autoFilled, userHasEdited dependencies

  const handleSelectSymbol = (option: SymbolOption) => {
    latestQueryRef.current = option.symbol;
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }
    setQuery(option.symbol);
    setSelectedInstrumentName(option.name); // Store instrument name
    onChangeText(option.symbol);
    // Notify parent component with full option data (including currency)
    if (onSymbolSelect) {
      onSymbolSelect(option);
    }
    setShowSuggestions(false);
  };

  const handleChangeText = (text: string) => {
    // Clear the pre-selected flag and mark as user-edited when user manually edits
    if (hasSelectedInitial && text !== value) {
      setHasSelectedInitial(false);
      setUserHasEdited(true);
    }
    // Also set userHasEdited if they type anything different from initial
    if (text !== value) {
      setUserHasEdited(true);
    }
    // Clear instrument name when user manually types
    setSelectedInstrumentName(undefined);
    setQuery(text);
    onChangeText(text);
  };

  const handleClear = () => {
    latestQueryRef.current = '';
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }
    setQuery('');
    onChangeText('');
    setShowSuggestions(false);
    setSuggestions([]);
  };

  const handleFocus = () => {
    // Don't show suggestions in readOnly mode
    if (readOnly) {
      return;
    }
    // Mark that user has focused the input
    setUserHasFocused(true);
    // Don't auto-show suggestions if we have a pre-selected initial value AND user hasn't edited
    // User can still type to trigger search
    // Exception: if autoFilled is true (e.g., AI extraction), allow suggestions
    if (hasSelectedInitial && !userHasEdited && !autoFilled) {
      return;
    }
    if (query.length > 0) {
      setShowSuggestions(true);
    } else if (positionSymbols.length > 0) {
      setSuggestions(positionSymbols.slice(0, 10));
      setShowSuggestions(true);
    }
  };

  return (
    <View style={styles.container}>
      {readOnly ? (
        <View>
          <View
            style={[styles.inputContainer, styles.readOnlyContainer, error && styles.inputError]}
          >
            <Text style={[styles.readOnlyText, { color: theme.colors.text }]}>{query}</Text>
          </View>
          {(selectedInstrumentName || propInstrumentName) && (
            <Text style={[styles.instrumentNameText, { color: theme.colors.textSecondary }]}>
              {selectedInstrumentName || propInstrumentName}
            </Text>
          )}
        </View>
      ) : (
        <View>
          <View style={[styles.inputContainer, error && styles.inputError]}>
            <Ionicons
              name="search"
              size={20}
              color={theme.colors.textTertiary}
              style={styles.searchIcon}
            />
            <TextInput
              style={[styles.input, { color: theme.colors.text }]}
              value={query}
              onChangeText={handleChangeText}
              onFocus={handleFocus}
              onBlur={() => {
                setTimeout(() => setShowSuggestions(false), 200);
              }}
              placeholder={
                placeholder || t('portfolio.symbolPlaceholder', { defaultValue: 'e.g., AAPL' })
              }
              placeholderTextColor={theme.colors.textTertiary}
              autoCapitalize="characters"
              autoCorrect={false}
              editable={editable}
            />
            {query.length > 0 && editable && (
              <TouchableOpacity
                onPress={handleClear}
                style={styles.clearButton}
                hitSlop={{ top: 8, bottom: 8, left: 8, right: 8 }}
              >
                <Ionicons name="close-circle" size={18} color={theme.colors.textTertiary} />
              </TouchableOpacity>
            )}
          </View>
          {(selectedInstrumentName || propInstrumentName) && (
            <Text style={[styles.instrumentNameText, { color: theme.colors.textSecondary }]}>
              {selectedInstrumentName || propInstrumentName}
            </Text>
          )}
        </View>
      )}
      {error && <Text style={[styles.errorText, { color: theme.colors.error }]}>{error}</Text>}
      {showSuggestions && (!hasSelectedInitial || userHasEdited || autoFilled) && (
        <View
          style={[
            styles.suggestionsContainer,
            { backgroundColor: theme.colors.surface, borderColor: theme.colors.border },
          ]}
        >
          <ScrollView keyboardShouldPersistTaps="handled">
            {loading ? (
              <View style={styles.loadingContainer}>
                <ActivityIndicator size="small" color={theme.colors.primary} />
                <Text style={[styles.loadingText, { color: theme.colors.textSecondary }]}>
                  {t('common.searching', { defaultValue: 'Searching...' })}
                </Text>
              </View>
            ) : suggestions.length === 0 ? (
              <View style={styles.noResultsContainer}>
                <Ionicons name="search" size={24} color={theme.colors.textTertiary} />
                <Text style={[styles.noResultsText, { color: theme.colors.textTertiary }]}>
                  {t('portfolio.noSymbolsFound', { defaultValue: 'No symbols found' })}
                </Text>
              </View>
            ) : (
              suggestions.map((option, index) => {
                const showExchange = !!option.exchange;
                const showCurrency = !!option.currency;
                const showName = !!option.name;
                const showBadge = option.source === 'position';
                return (
                  <TouchableOpacity
                    key={`${option.symbol}-${option.source}-${index}`}
                    style={styles.suggestionItem}
                    onPress={() => handleSelectSymbol(option)}
                  >
                    <View style={styles.suggestionContent}>
                      <View style={styles.suggestionMain}>
                        <Text style={[styles.suggestionSymbol, { color: theme.colors.text }]}>
                          {option.symbol}
                        </Text>
                        {showExchange ? (
                          <Text
                            style={[
                              styles.suggestionCurrency,
                              { color: theme.colors.textTertiary },
                            ]}
                          >
                            {option.exchange}
                          </Text>
                        ) : null}
                        {showCurrency ? (
                          <Text
                            style={[
                              styles.suggestionCurrency,
                              { color: theme.colors.textTertiary },
                            ]}
                          >
                            {showExchange ? ' ' : ''}
                            {option.currency}
                          </Text>
                        ) : null}
                      </View>
                      {showName ? (
                        <Text
                          style={[styles.suggestionName, { color: theme.colors.textSecondary }]}
                          numberOfLines={1}
                        >
                          {option.name}
                        </Text>
                      ) : null}
                      {showBadge ? (
                        <View
                          style={[
                            styles.positionBadge,
                            { backgroundColor: theme.colors.surfaceSecondary },
                          ]}
                        >
                          <Text style={[styles.positionBadgeText, { color: theme.colors.primary }]}>
                            {t('portfolio.position', { defaultValue: 'Position' })}
                          </Text>
                        </View>
                      ) : null}
                    </View>
                  </TouchableOpacity>
                );
              })
            )}
          </ScrollView>
        </View>
      )}
    </View>
  );
}

function createThemedStyles(theme: any) {
  return StyleSheet.create({
    container: {},
    inputContainer: {
      flexDirection: 'row',
      alignItems: 'center',
      borderWidth: 1,
      borderRadius: 8,
      padding: 12,
      backgroundColor: theme.colors.backgroundElevated,
      borderColor: theme.colors.border,
    },
    inputError: {
      borderColor: theme.colors.error,
    },
    searchIcon: {
      marginRight: 8,
    },
    input: {
      flex: 1,
      fontSize: 16,
    },
    readOnlyContainer: {
      backgroundColor: theme.colors.backgroundSecondary,
      opacity: 0.8,
    },
    readOnlyText: {
      flex: 1,
      fontSize: 16,
      fontWeight: '600',
    },
    instrumentNameText: {
      fontSize: 13,
      marginTop: 4,
      fontStyle: 'italic',
    },
    clearButton: {
      marginLeft: 8,
    },
    errorText: {
      fontSize: 12,
      marginTop: 4,
    },
    suggestionsContainer: {
      marginTop: 4,
      borderRadius: 8,
      borderWidth: 1,
      maxHeight: 250,
      elevation: 4,
      shadowColor: '#000',
      shadowOffset: { width: 0, height: 2 },
      shadowOpacity: 0.1,
      shadowRadius: 4,
    },
    suggestionsScroll: {
      maxHeight: 250,
    },
    loadingContainer: {
      padding: 20,
      alignItems: 'center',
    },
    loadingText: {
      fontSize: 14,
      marginTop: 8,
    },
    noResultsContainer: {
      padding: 30,
      alignItems: 'center',
    },
    noResultsText: {
      fontSize: 14,
      marginTop: 8,
    },
    suggestionItem: {
      paddingVertical: 12,
      paddingHorizontal: 16,
      borderBottomWidth: 1,
      borderBottomColor: 'transparent',
    },
    suggestionContent: {},
    suggestionMain: {
      flexDirection: 'row',
      alignItems: 'center',
    },
    suggestionSymbol: {
      fontSize: 16,
      fontWeight: '600',
      marginRight: 8,
    },
    suggestionCurrency: {
      fontSize: 12,
    },
    suggestionName: {
      fontSize: 13,
      marginTop: 4,
    },
    positionBadge: {
      alignSelf: 'flex-start',
      paddingHorizontal: 6,
      paddingVertical: 2,
      borderRadius: 4,
      marginTop: 4,
    },
    positionBadgeText: {
      fontSize: 10,
      fontWeight: '600',
      textTransform: 'uppercase',
    },
  });
}
